import { Layers } from '@/static/Layers'

export enum EffectGroupName {
  NONE = 'None',
  VHS = 'VHS',
  OLD_CAMERA = 'Old Camera',
  CHROMA_SHIFT = 'Chroma Shift',
  BLUR = 'Bass Blur',
  MIRROR = 'Mirror',
  PIXELATE = 'Pixelate',
  WOBBLE = 'Wobble',
  NEON = 'Neon',
  DUOTONE = 'Duotone',
  FILM = 'Film',
  OLD_TV = 'Old TV'
}

export enum EffectName {
  NONE = 'None',
  CHROMA_SHIFT = 'Chroma Shift',
  RUBY_DUO = 'Ruby Duo',
  GRANITE_DUO = 'Granite Duo',
  PEACH_DUO = 'Peach Duo',
  DAMSON_DUO = 'Damson Duo',
  GUAVA_DUO = 'Guava Duo',
  GRAPE_DUO = 'Grape Duo',
  LYCHEE_DUO = 'Lychee Duo',
  LIME_DUO = 'Lime Duo',
  GRAPHITE_DUO = 'Graphite Duo',
  PIXELATE = 'Pixelate',
  OLD_TV = 'Old TV',
  NEON = 'Neon',
  NEON_BLEND = 'Neon Blend',
  RADIAL_BLUR = 'Radial Blur',
  ZOOM_BLUR = 'Zoom Blur',
  MOTION_BLUR = 'Motion Blur',
  FADED_FILM = 'Faded Film',
  VINTAGE_FILM = 'Vintage Film',
  CONTRAST_FILM = 'Contrast Film',
  SEPIA_FILM = 'Sepia Film',
  COOL_FILM = 'Cool Film',
  MIRROR_VERTICAL = 'Vertical Mirror',
  MIRROR_VERTICAL_FLIPPED = 'Flipped Vertical Mirror',
  MIRROR_HORIZONTAL = 'Horizontal Mirror',
  MIRROR_HORIZONTAL_FLIPPED = 'Flipped Horizontal Mirror',
  MIRROR_QUAD = 'Quad Mirror',
  KALEIDOSCOPE_QUAD = 'Quad Kaleidoscope',
  KALEIDOSCOPE_OCTO = 'Octo Kaleidoscope',
  VHS = 'VHS',
  OLD_CAMERA = 'Old Camera',
  DISPLACEMENT = 'Wobble'
}

export interface EffectGroup {
  name: EffectGroupName
  effects: AvailableEffect[]
}

export interface AvailableEffect {
  name: EffectName
  thumbnail: string
  config: EffectConfig,
  groupName: EffectGroupName
}

export interface EffectConfig {
  isAudioReactive: boolean
  isAudioReactivityEnabledByDefault: boolean
  defaultIntensityValue: number
  layers: Layers[]
}

const duoToneConfig = {
  isAudioReactive: false,
  isAudioReactivityEnabledByDefault: false,
  defaultIntensityValue: 1.0,
  layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER]
}

const vintageConfig = {
  isAudioReactive: false,
  isAudioReactivityEnabledByDefault: false,
  defaultIntensityValue: 1.0,
  layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
}

const mirrorConfig = {
  isAudioReactive: true,
  isAudioReactivityEnabledByDefault: true,
  defaultIntensityValue: 0.5,
  layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
}

const kaleidoscopeConfig = {
  isAudioReactive: true,
  isAudioReactivityEnabledByDefault: true,
  defaultIntensityValue: 0.6,
  layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
}

/**
 * Not all effects should be available for users.
 * Some of the effects, like posterization, will be used as components of more complex effects.
 * The effects below are to be removed by the production ones as we implement them.
 */
export const effectGroups: EffectGroup[] = [
  {
    name: EffectGroupName.VHS,
    effects: [
      {
        name: EffectName.VHS,
        groupName: EffectGroupName.VHS,
        thumbnail: 'vis-vhs',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 0.6,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.OLD_CAMERA,
    effects: [
      {
        name: EffectName.OLD_CAMERA,
        groupName: EffectGroupName.OLD_CAMERA,
        thumbnail: 'vis-old-cam',
        config: {
          isAudioReactive: false,
          isAudioReactivityEnabledByDefault: false,
          defaultIntensityValue: 1.0,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.CHROMA_SHIFT,
    effects: [
      {
        name: EffectName.CHROMA_SHIFT,
        groupName: EffectGroupName.CHROMA_SHIFT,
        thumbnail: 'vis-rgb',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 0.14,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.BLUR,
    effects: [
      {
        name: EffectName.MOTION_BLUR,
        groupName: EffectGroupName.BLUR,
        thumbnail: 'vis-motion-blur',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 1.0,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      },
      {
        name: EffectName.ZOOM_BLUR,
        groupName: EffectGroupName.BLUR,
        thumbnail: 'vis-zoom-blur',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 1.0,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      },
      {
        name: EffectName.RADIAL_BLUR,
        groupName: EffectGroupName.BLUR,
        thumbnail: 'vis-radial-blur',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 0.3,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.MIRROR,
    effects: [
      {
        name: EffectName.MIRROR_HORIZONTAL,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-horizontal-mirror',
        config: mirrorConfig
      },
      {
        name: EffectName.MIRROR_HORIZONTAL_FLIPPED,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-flipped-horizontal-mirror',
        config: mirrorConfig
      },
      {
        name: EffectName.MIRROR_VERTICAL,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-vertical-mirror',
        config: mirrorConfig
      },
      {
        name: EffectName.MIRROR_VERTICAL_FLIPPED,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-flipped-vertical-mirror',
        config: mirrorConfig
      },
      {
        name: EffectName.MIRROR_QUAD,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-quad-mirror',
        config: mirrorConfig
      },
      {
        name: EffectName.KALEIDOSCOPE_QUAD,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-quad-kaleidoscope',
        config: kaleidoscopeConfig
      },
      {
        name: EffectName.KALEIDOSCOPE_OCTO,
        groupName: EffectGroupName.MIRROR,
        thumbnail: 'vis-octo-kaleidoscope',
        config: kaleidoscopeConfig
      }
    ]
  },
  {
    name: EffectGroupName.PIXELATE,
    effects: [
      {
        name: EffectName.PIXELATE,
        groupName: EffectGroupName.PIXELATE,
        thumbnail: 'vis-pixel',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 0.35,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.WOBBLE,
    effects: [
      {
        name: EffectName.DISPLACEMENT,
        groupName: EffectGroupName.WOBBLE,
        thumbnail: 'vis-wobble',
        config: {
          isAudioReactive: false,
          isAudioReactivityEnabledByDefault: false,
          defaultIntensityValue: 0.6,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.NEON,
    effects: [
      {
        name: EffectName.NEON_BLEND,
        groupName: EffectGroupName.NEON,
        thumbnail: 'vis-neon-blend',
        config: {
          isAudioReactive: false,
          isAudioReactivityEnabledByDefault: false,
          defaultIntensityValue: 0.5,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      },
      {
        name: EffectName.NEON,
        groupName: EffectGroupName.NEON,
        thumbnail: 'vis-edge',
        config: {
          isAudioReactive: false,
          isAudioReactivityEnabledByDefault: false,
          defaultIntensityValue: 0.5,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  },
  {
    name: EffectGroupName.DUOTONE,
    effects: [
      {
        name: EffectName.RUBY_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-ruby',
        config: duoToneConfig
      },
      {
        name: EffectName.GRANITE_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-granite',
        config: duoToneConfig
      },
      {
        name: EffectName.PEACH_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-peach',
        config: duoToneConfig
      },
      {
        name: EffectName.DAMSON_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-damson',
        config: duoToneConfig
      },
      {
        name: EffectName.GUAVA_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-guava',
        config: duoToneConfig
      },
      {
        name: EffectName.GRAPE_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-grape',
        config: duoToneConfig
      },
      {
        name: EffectName.LYCHEE_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-lychee',
        config: duoToneConfig
      },
      {
        name: EffectName.LIME_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-lime',
        config: duoToneConfig
      },
      {
        name: EffectName.GRAPHITE_DUO,
        groupName: EffectGroupName.DUOTONE,
        thumbnail: 'vis-duo-graphite',
        config: duoToneConfig
      }
    ]
  },
  {
    name: EffectGroupName.FILM,
    effects: [
      {
        name: EffectName.VINTAGE_FILM,
        groupName: EffectGroupName.FILM,
        thumbnail: 'vis-vintage',
        config: vintageConfig
      },
      {
        name: EffectName.FADED_FILM,
        groupName: EffectGroupName.FILM,
        thumbnail: 'vis-faded',
        config: vintageConfig
      },
      {
        name: EffectName.CONTRAST_FILM,
        groupName: EffectGroupName.FILM,
        thumbnail: 'vis-contrast',
        config: vintageConfig
      },
      {
        name: EffectName.SEPIA_FILM,
        groupName: EffectGroupName.FILM,
        thumbnail: 'vis-sepia',
        config: vintageConfig
      },
      {
        name: EffectName.COOL_FILM,
        groupName: EffectGroupName.FILM,
        thumbnail: 'vis-cool',
        config: vintageConfig
      }
    ]
  },
  {
    name: EffectGroupName.OLD_TV,
    effects: [
      {
        name: EffectName.OLD_TV,
        groupName: EffectGroupName.OLD_TV,
        thumbnail: 'vis-old-tv',
        config: {
          isAudioReactive: true,
          isAudioReactivityEnabledByDefault: true,
          defaultIntensityValue: 0.5,
          layers: [Layers.BACKGROUND, Layers.LOGO, Layers.STICKER, Layers.TEXT]
        }
      }
    ]
  }
]
